import { useEffect, useState } from "react";
import { Phone, MessageCircle, Sparkles, Calendar, Check, Image as ImageIcon } from "lucide-react";

/**
 * Three live, animated mockups demonstrating AI Automate Systems capabilities.
 * No external video — pure CSS/JS so the section is impactful without placeholder content.
 */
export default function ShowcaseVideo() {
  return (
    <section id="showcase" className="py-24 lg:py-32 bg-ink text-cream relative overflow-hidden">
      <div className="absolute -left-32 top-1/3 w-[500px] h-[500px] rounded-full bg-rust/15 blur-3xl pointer-events-none"></div>

      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 relative">
        <div className="reveal flex items-end justify-between flex-wrap gap-6 mb-14">
          <div className="max-w-[36ch]">
            <span className="tag tag-dark mb-4">
              <Sparkles size={12} className="text-ember" /> Live demo
            </span>
            <h2 className="display text-5xl lg:text-7xl mt-5">
              See it run. <span className="display-italic text-ember">Right here.</span>
            </h2>
            <p className="text-cream/70 mt-5 max-w-[48ch]">
              Three flows on autoplay: an AI receptionist closing a midnight booking, AI-generated social content, and a DM setter dragging a cold lead into a booked job.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          <ReceptionistDemo />
          <ContentDemo />
          <DmSetterDemo />
        </div>
      </div>
    </section>
  );
}

/* ---------- Demo 1: AI Receptionist (animated call transcript) ---------- */
function ReceptionistDemo() {
  const lines = [
    { who: "caller", text: "Hi — do you have anything tonight?" },
    { who: "ai", text: "We're closed at 9, but I can book you Thursday at 2:30. Need a name and number?" },
    { who: "caller", text: "Diana Méndez, 555-0142." },
    { who: "ai", text: "Booked Thu 2:30 PM. Confirmation just texted to you." },
  ];
  const [shown, setShown] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setShown((n) => (n >= lines.length ? 0 : n + 1)), 2000);
    return () => clearInterval(t);
  }, [lines.length]);

  return (
    <div className="reveal rounded-2xl bg-paper text-ink p-5 lift" data-testid="reel-0">
      <Header
        Icon={Phone}
        title="AI Receptionist"
        sub="Riverbend Dental · 11:43 PM"
        tag="Live · 0:52"
      />
      <div className="mt-5 h-[260px] overflow-hidden flex flex-col gap-2.5 px-1">
        {lines.slice(0, shown).map((l, i) => (
          <div
            key={i}
            className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-snug animate-fade-up ${
              l.who === "ai"
                ? "bg-ink text-cream self-start rounded-tl-sm"
                : "bg-cream border border-ink/10 self-end rounded-tr-sm"
            }`}
          >
            {l.text}
          </div>
        ))}
        {shown >= lines.length && (
          <div className="self-start mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rust/10 text-rust mono text-[10px] uppercase tracking-wider animate-fade-up">
            <Check size={12} /> Booked · intake form sent
          </div>
        )}
      </div>
      <div className="mt-4 pt-4 border-t border-ink/10 flex items-center justify-between text-xs">
        <span className="mono text-mute uppercase tracking-wider">Avg pickup</span>
        <span className="display text-rust text-xl">0.4s</span>
      </div>
    </div>
  );
}

/* ---------- Demo 2: AI Content (animated reel carousel) ---------- */
function ContentDemo() {
  const slides = [
    { tone: "Before", body: "Posting once a month. Crickets.", color: "bg-mute/60" },
    { tone: "After", body: "5 reels/wk · captions · hashtags", color: "bg-rust" },
    { tone: "Result", body: "+312% reach in 30 days", color: "bg-ember" },
  ];
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((n) => (n + 1) % slides.length), 2200);
    return () => clearInterval(t);
  }, [slides.length]);

  return (
    <div className="reveal rounded-2xl bg-paper text-ink p-5 lift" data-testid="reel-1">
      <Header
        Icon={ImageIcon}
        title="AI Content Engine"
        sub="Glow Med Spa · weekly drop"
        tag="Auto · 0:18"
      />

      <div className="mt-5 relative h-[260px] rounded-xl overflow-hidden bg-ink">
        {slides.map((s, idx) => (
          <div
            key={idx}
            className={`absolute inset-0 transition-all duration-700 ${
              idx === i ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            <div className={`absolute inset-0 ${s.color}`}></div>
            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent"></div>
            <div className="absolute top-4 left-4 mono text-[10px] uppercase tracking-wider text-cream/90 bg-ink/40 backdrop-blur px-2 py-1 rounded-full">
              {s.tone}
            </div>
            <div className="absolute bottom-5 left-5 right-5">
              <div className="display text-cream text-2xl leading-tight">{s.body}</div>
            </div>
          </div>
        ))}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
          {slides.map((_, idx) => (
            <span key={idx} className={`h-1 rounded-full transition-all ${idx === i ? "w-6 bg-cream" : "w-2 bg-cream/40"}`}></span>
          ))}
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-ink/10 flex items-center justify-between text-xs">
        <span className="mono text-mute uppercase tracking-wider">Posts / wk</span>
        <span className="display text-rust text-xl">5–7</span>
      </div>
    </div>
  );
}

/* ---------- Demo 3: DM Setter ---------- */
function DmSetterDemo() {
  const steps = [
    { label: "Cold IG DM", body: '"Yo, do you guys handle metal roofs?"' },
    { label: "AI replies < 60s", body: '"We do. What city, and is this for repair or replace?"' },
    { label: "Lead qualifies", body: '"Tucson, full replace. Place is 1,800 sqft."' },
    { label: "Booked", body: "Wed 9am estimate · est. $14,500" },
  ];
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep((n) => (n + 1) % steps.length), 1800);
    return () => clearInterval(t);
  }, [steps.length]);

  return (
    <div className="reveal rounded-2xl bg-paper text-ink p-5 lift" data-testid="reel-2">
      <Header
        Icon={MessageCircle}
        title="DM Setter"
        sub="Apex Roofing · IG inbound"
        tag="Auto · 0:34"
      />
      <ol className="mt-5 space-y-3 h-[260px]">
        {steps.map((s, idx) => (
          <li
            key={idx}
            className={`flex gap-3 transition-all duration-500 ${
              idx <= step ? "opacity-100 translate-y-0" : "opacity-30 translate-y-1"
            }`}
          >
            <div
              className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center mono text-[11px] ${
                idx === step
                  ? "bg-rust text-cream"
                  : idx < step
                  ? "bg-ink text-cream"
                  : "bg-ink/10 text-ink/40"
              }`}
            >
              {idx < step ? <Check size={12} /> : idx + 1}
            </div>
            <div className="flex-1">
              <div className="mono text-[10px] uppercase tracking-wider text-mute">{s.label}</div>
              <div className="text-sm leading-snug mt-0.5">{s.body}</div>
            </div>
          </li>
        ))}
      </ol>
      <div className="mt-4 pt-4 border-t border-ink/10 flex items-center justify-between text-xs">
        <span className="mono text-mute uppercase tracking-wider">Reply time</span>
        <span className="display text-rust text-xl flex items-center gap-1.5"><Calendar size={14} /> &lt; 60s</span>
      </div>
    </div>
  );
}

function Header({ Icon, title, sub, tag }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="svc-icon"><Icon size={18} strokeWidth={1.6} /></div>
        <div>
          <div className="display text-lg leading-tight">{title}</div>
          <div className="text-xs text-mute">{sub}</div>
        </div>
      </div>
      <span className="mono text-[10px] uppercase tracking-wider text-mute bg-ink/5 px-2 py-1 rounded-full whitespace-nowrap">{tag}</span>
    </div>
  );
}
